import { useCallback, useEffect, useState, useRef } from 'react'
import { useModules } from '../hooks/useModules'
import { Link, useNavigate } from 'react-router-dom'
import {
  Headphones,
  Search,
  Plus,
  X,
  Mic,
  Loader2,
  FileAudio,
  MessageSquare,
  Phone,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import type { Ticket } from '../types'
import { formatDate, getTicketStatusLabel, getTicketStatusColor } from '../utils/helpers'
import TicketChatbot from '../components/TicketChatbot'

export default function Tickets() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { enabledModules, disableModule } = useModules()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    type_code: '',
    category_code: '',
    severity_code: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [transcribing, setTranscribing] = useState(false)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const [showChatbot, setShowChatbot] = useState(false)
  const [chatbotEnabled, setChatbotEnabled] = useState(false)
  const [vapiEnabled, setVapiEnabled] = useState(false)
  const [vapiPhone, setVapiPhone] = useState<string | null>(null)

  const loadTickets = useCallback(async () => {
    try {
      const res = await api.getTickets()
      const data: Ticket[] = Array.isArray(res) ? res : ((res as { data?: Ticket[] })?.data ?? [])
      setTickets(data)
      setFilteredTickets(data)
      setError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { status?: number } }
      if (apiErr?.response?.status === 403) {
        disableModule('tickets')
        navigate('/dashboard', { replace: true })
        return
      }
      console.error('Erreur chargement tickets:', err)
      setError(getApiErrorKey(err, 'tickets.loadError'))
    } finally {
      setLoading(false)
    }
  }, [disableModule, navigate])

  const filterTickets = useCallback(() => {
    if (!searchTerm) {
      setFilteredTickets(tickets)
      return
    }
    const filtered = tickets.filter(
      (ticket) =>
        ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.track_id.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredTickets(filtered)
  }, [tickets, searchTerm])

  useEffect(() => {
    if (enabledModules.tickets === false) {
      navigate('/dashboard', { replace: true })
      return
    }
    loadTickets()
    api
      .getChatbotConfig()
      .then((cfg) => setChatbotEnabled(cfg.enabled))
      .catch(() => {})
    api
      .getVapiConfig()
      .then((cfg) => {
        setVapiEnabled(cfg.enabled)
        setVapiPhone(cfg.phone_number ?? null)
      })
      .catch(() => {})
  }, [enabledModules.tickets, loadTickets, navigate])

  useEffect(() => {
    filterTickets()
  }, [filterTickets])

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      if (audioFile) {
        setTranscribing(true)
        await api.createTicketWithAudio(newTicket, audioFile)
      } else {
        await api.createTicket(newTicket)
      }
      setShowNewTicketModal(false)
      setNewTicket({
        subject: '',
        message: '',
        type_code: '',
        category_code: '',
        severity_code: '',
      })
      setAudioFile(null)
      loadTickets()
    } catch (error) {
      console.error('Erreur création ticket:', error)
      alert(t('tickets.createError'))
    } finally {
      setSubmitting(false)
      setTranscribing(false)
    }
  }

  const handleAudioFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Vérifier la taille (max 25MB pour Whisper)
    if (file.size > 25 * 1024 * 1024) {
      alert(t('tickets.audioSizeError'))
      return
    }
    setAudioFile(file)
  }

  const openTickets = filteredTickets.filter((t) => t.status !== 9 && t.status !== 'closed').length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium mb-2">{t(error)}</p>
          <button
            onClick={loadTickets}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 flex-1 mr-4">
          <p className="text-sm font-medium text-gray-600">{t('tickets.openTickets')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{openTickets}</p>
        </div>
        <div className="flex gap-2">
          {chatbotEnabled && (
            <button
              onClick={() => setShowChatbot(true)}
              className="px-5 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <MessageSquare className="w-5 h-5" />
              {t('chatbot.openButton')}
            </button>
          )}
          <button
            onClick={() => setShowNewTicketModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('tickets.newTicket')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('tickets.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Mobile card layout */}
        <ul className="divide-y divide-gray-100 sm:hidden">
          {filteredTickets.length === 0 ? (
            <li className="p-6 text-center text-gray-500">
              <Headphones className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p>{t('tickets.notFound')}</p>
            </li>
          ) : (
            filteredTickets.map((ticket) => (
              <li key={ticket.id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to={`/tickets/${ticket.id}`}
                    className="font-medium text-primary-600 hover:text-primary-700"
                  >
                    {ticket.ref}
                  </Link>
                  <span
                    className={`flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTicketStatusColor(ticket.status)}`}
                  >
                    {getTicketStatusLabel(ticket.status, t)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900">{ticket.subject}</p>
                <p className="text-xs text-gray-500">
                  {t('tickets.trackId', { id: ticket.track_id })}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-gray-400">{formatDate(ticket.date_creation)}</span>
                  <div className="flex items-center gap-2 flex-1 max-w-[140px]">
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-primary-600 h-1.5 rounded-full"
                        style={{ width: `${ticket.progress || 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 whitespace-nowrap">
                      {ticket.progress || 0}%
                    </span>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>

        {/* Desktop table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tickets.colRef')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tickets.colSubject')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tickets.colDate')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tickets.colStatus')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('tickets.colProgress')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <Headphones className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>{t('tickets.notFound')}</p>
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/tickets/${ticket.id}`}
                        className="font-medium text-primary-600 hover:text-primary-700"
                      >
                        {ticket.ref}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{ticket.subject}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('tickets.trackId', { id: ticket.track_id })}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(ticket.date_creation)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getTicketStatusColor(ticket.status)}`}
                      >
                        {getTicketStatusLabel(ticket.status, t)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${ticket.progress || 0}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600">{ticket.progress || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vapi phone support card */}
      {vapiEnabled && vapiPhone && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-4 flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-full">
            <Phone className="w-6 h-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{t('tickets.phoneSupport')}</p>
            <p className="text-sm text-gray-600">{t('tickets.phoneSupportHint')}</p>
          </div>
          <a
            href={`tel:${vapiPhone}`}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
          >
            <Phone className="w-4 h-4" />
            {vapiPhone}
          </a>
        </div>
      )}

      {/* SAV Chatbot */}
      {showChatbot && (
        <TicketChatbot
          onClose={() => setShowChatbot(false)}
          onTicketCreated={() => {
            setShowChatbot(false)
            loadTickets()
          }}
        />
      )}

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">{t('tickets.modalTitle')}</h2>
              <button
                onClick={() => setShowNewTicketModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tickets.subjectLabel')}
                </label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('tickets.subjectPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tickets.messageLabel')}
                </label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  placeholder={t('tickets.messagePlaceholder')}
                />
              </div>

              {/* Fichier audio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tickets.audioLabel')}
                </label>
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mp3,audio/wav,audio/webm,audio/m4a,audio/mp4,audio/mpeg,audio/mpga,.mp3,.wav,.webm,.m4a"
                  onChange={handleAudioFileChange}
                  className="hidden"
                />
                {audioFile ? (
                  <div className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                    <FileAudio className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-sm text-primary-800 truncate flex-1">
                      {audioFile.name}
                    </span>
                    <span className="text-xs text-primary-500">
                      {(audioFile.size / 1024 / 1024).toFixed(1)} Mo
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setAudioFile(null)
                        if (audioInputRef.current) audioInputRef.current.value = ''
                      }}
                      className="p-1 hover:bg-primary-100 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-primary-600" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => audioInputRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors"
                  >
                    <Mic className="w-5 h-5" />
                    {t('tickets.audioButton')}
                  </button>
                )}
                <p className="mt-1 text-xs text-gray-400">{t('tickets.audioHint')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tickets.typeLabel')}
                  </label>
                  <select
                    value={newTicket.type_code}
                    onChange={(e) => setNewTicket({ ...newTicket, type_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="ISSUE">{t('tickets.typeIssue')}</option>
                    <option value="QUESTION">{t('tickets.typeQuestion')}</option>
                    <option value="REQUEST">{t('tickets.typeRequest')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tickets.categoryLabel')}
                  </label>
                  <select
                    value={newTicket.category_code}
                    onChange={(e) => setNewTicket({ ...newTicket, category_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="TECHNICAL">{t('tickets.catTechnical')}</option>
                    <option value="BILLING">{t('tickets.catBilling')}</option>
                    <option value="OTHER">{t('tickets.catOther')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('tickets.priorityLabel')}
                  </label>
                  <select
                    value={newTicket.severity_code}
                    onChange={(e) => setNewTicket({ ...newTicket, severity_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="LOW">{t('tickets.priLow')}</option>
                    <option value="NORMAL">{t('tickets.priNormal')}</option>
                    <option value="HIGH">{t('tickets.priHigh')}</option>
                    <option value="CRITICAL">{t('tickets.priCritical')}</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {transcribing
                    ? t('tickets.transcribing')
                    : submitting
                      ? t('tickets.creating')
                      : t('tickets.createButton')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
